// @flow
import React, {useState, useEffect} from 'react';
import type {Node} from 'react';
import moment from 'moment';
import {useMutation} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {groupsCreate, groupsEventsCreate} from 'api/v2';
import type {Group} from 'api/v2';
import {Box, Stack} from '@wellstone-solutions/web';
import {BridgeEventSources, Hooks} from '@wellstone-solutions/common';
import {useStores} from 'hooks/useStores';
import {useEventSource} from 'hooks/useEventSource';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {PageHeader} from 'components/PageHeader';
import {ActionButtons, Form, EventFormList} from './Form';
import {
  groupSchema,
  groupEventSchema,
  MAX_NUM_GROUP_EVENTS,
} from '../constants';
import {toV2Api} from '../utils';

const FORM_ID = 'new_group_form';

export const New = (): Node => {
  // eventsUI is the state used to control the visibility of group events
  const [eventsUI, setEventsUI] = useState(
    Array(MAX_NUM_GROUP_EVENTS)
      .fill()
      .map((_, i) => ({isVisible: i === 0, isOpen: false})),
  );
  const {calendarStore, groupStore, programStore, meStore, appUIStore} =
    useStores();

  useEventSource(BridgeEventSources.GROUPS_PAGE);

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);
  }, []);

  const mutation = useMutation({
    mutationFn: groupsCreate,
    onSuccess: () => {},
  });

  const eventMutation = useMutation({
    mutationFn: ({groupId, event}) => groupsEventsCreate(groupId, event),
  });

  const navigate = useNavigate();

  // Not using model Factory due to new apiv2 shape
  const group: Group = {
    name: '',
    description: '',
    isPublic: true,
    organization: meStore.me.membership.organization.id,
    programs: [],
    category: '',
  };

  // pre-allocate hooks to avoid re-rendering issues
  const groupEventForms = [];
  for (let i = 0; i < MAX_NUM_GROUP_EVENTS; i++) {
    groupEventForms[i] = Hooks.useForm({
      initialValues: groupEventSchema.cast({timezone: moment.tz.guess()}),
      schema: groupEventSchema,
      validationEngine: 'yup',
    });
  }

  const form = Hooks.useForm({
    initialValues: group,
    schema: groupSchema,
    validationEngine: 'yup',
    onSubmit: async (values, {resetForm}) => {
      try {
        const response = await mutation.mutateAsync(values);

        // Get the valid group event forms and POST them
        const validGroupEventForms = groupEventForms.filter(
          (form) => form.dirty && form.isValid,
        );

        await Promise.all(
          validGroupEventForms.map((form) =>
            eventMutation.mutateAsync({
              groupId: response.data.id,
              event: {
                event: toV2Api(form.values),
                reminderTime: form.values.reminderTime,
              },
            }),
          ),
        );

        resetForm();

        // TODO: Replace these two fetches with RQ expiration pattern
        await programStore.getAllPrograms();
        await calendarStore.getAllCalendarEvents();

        Promise.all([
          groupStore.loadGroups(meStore.me.membership.organization.id, false),
          groupStore.requestMyGroups(meStore.permittedGroupIds),
        ]);
        // End replace

        navigate(`/groups/${response.data.id}`);
      } catch (error) {
        if (error.request.responseURL.search(/\/events(\/|$|\?|#)/) !== -1) {
          showAlert(`Unable to save scheduled event.`, ALERT_TYPES.ERROR);
          Object.entries(error.response?.data).forEach(([key, values]) => {
            showAlert(values, ALERT_TYPES.ERROR);
          });
        } else {
          Object.entries(error.response?.data).forEach(([key, values]) => {
            form.setFieldError(key, values.join(' '));
          });
          showAlert(`Unable to save new group.`, ALERT_TYPES.ERROR);
        }
      }
    },
  });

  return (
    <Box>
      <Stack direction={{xs: 'column', md: 'row'}}>
        <Stack
          flex={1}
          direction="column"
          justifyContent={{md: 'space-between'}}
          sx={{mb: 4}}>
          <PageHeader
            title="Create Group Details"
            description="All fields are required unless indicated as optional."
            backButtonProps={{
              label: 'Back to Groups',
              path: '/groups',
            }}
          />

          <Form form={form} formId={FORM_ID} />

          <EventFormList
            forms={groupEventForms}
            eventsUI={eventsUI}
            setEventsUI={setEventsUI}
          />

          <ActionButtons
            formId={FORM_ID}
            form={form}
            groupEventForms={groupEventForms}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
